import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { RefreshCcw, Zap } from "lucide-react";

interface Bot {
  id: string;
  name: string;
  type: "automated" | "on-demand";
  stats: {
    queueSize?: number;
    patientCount?: number;
    schedule?: string;
  };
}

const BotsPage: React.FC = () => {
  const navigate = useNavigate();
  const [bots] = React.useState<Bot[]>([
    {
      id: "1",
      name: "Appointment-Reminder",
      type: "automated",
      stats: {
        queueSize: 45,
        schedule: "48hrs before appointment",
      },
    },
    {
      id: "2",
      name: "Insurance-Verify",
      type: "automated",
      stats: {
        queueSize: 23,
        schedule: "7 days before appointment",
      },
    },
    {
      id: "3",
      name: "ASAP-List",
      type: "on-demand",
      stats: {
        patientCount: 15,
      },
    },
    {
      id: "4",
      name: "Recall-System",
      type: "on-demand",
      stats: {
        patientCount: 89,
      },
    },
  ]);

  return (
    <div className="m-4 md:m-8">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Bots</CardTitle>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Automated Bots Section */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Automated Bots</h2>
            <div className="space-y-4">
              {bots
                .filter((bot) => bot.type === "automated")
                .map((bot) => (
                  <Card
                    key={bot.id}
                    className="hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/bots/${bot.id}`)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <RefreshCcw className="h-4 w-4 text-info" />
                            <span className="font-medium">{bot.name}</span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <div>Auto-Schedule: {bot.stats.schedule}</div>
                            <div>
                              Today's Queue: {bot.stats.queueSize} calls
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </div>

          {/* On-Demand Bots Section */}
          <div>
            <h2 className="text-lg font-semibold mb-4">On-Demand Bots</h2>
            <div className="space-y-4">
              {bots
                .filter((bot) => bot.type === "on-demand")
                .map((bot) => (
                  <Card
                    key={bot.id}
                    className="hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/bots/${bot.id}`)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-warning" />
                            <span className="font-medium">{bot.name}</span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            <div>
                              Current List: {bot.stats.patientCount} patients
                            </div>
                            <Button className="mt-2" size="sm">
                              Start Calls Now
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BotsPage;
